import React, { useEffect, useState } from 'react';
import './LandingPage.css';
import LandingNav from '../components/landing/LandingNav';
import PlatformCompanyInfo from '../components/landing/PlatformCompanyInfo';
import { loadPublicLandingFaq, peekCachedPublicLandingFaq } from '../utils/landingPublicContent';
import type { LandingFaqItem } from '../types/landingFaq';

interface LandingQaPageProps {
  onLoginClick: () => void;
}

const LandingQaPage: React.FC<LandingQaPageProps> = ({ onLoginClick }) => {
  const [items, setItems] = useState<LandingFaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const cached = peekCachedPublicLandingFaq();
        if (cached?.length) {
          setItems(cached);
          setLoading(false);
        }
        const fresh = await loadPublicLandingFaq();
        if (cancelled) return;
        setItems(fresh);
        setError(null);
        setLoading(false);
      } catch (err) {
        console.error('Q&A 로드 실패:', err);
        if (!cancelled) {
          if (!peekCachedPublicLandingFaq()?.length) {
            setError('Q&A를 불러올 수 없습니다.');
          }
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="landing-page landing-page--loading">
        <div className="landing-loading-spinner" aria-label="로딩 중" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page landing-page--error">
        <LandingNav onLoginClick={onLoginClick} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="landing-page landing-qa-page">
      <LandingNav onLoginClick={onLoginClick} />

      <main className="landing-qa-main">
        <header className="landing-qa-header">
          <h1 className="landing-qa-title">Q&amp;A</h1>
          <p className="landing-qa-lead">자주 묻는 질문을 확인해 보세요.</p>
        </header>

        <div className="landing-qa-list" role="list">
          {items.length === 0 ? (
            <p className="landing-qa-empty">등록된 질문이 없습니다.</p>
          ) : (
            items.map((item) => {
              const isOpen = openId === item.id;
              return (
                <div
                  key={item.id}
                  className={`landing-qa-item${isOpen ? ' is-open' : ''}`}
                  role="listitem"
                >
                  <button
                    type="button"
                    className="landing-qa-question"
                    aria-expanded={isOpen}
                    onClick={() => setOpenId(isOpen ? null : item.id)}
                  >
                    <span className="landing-qa-question-text">
                      {item.category ? (
                        <span className="landing-qa-category-tag">{item.category}</span>
                      ) : null}
                      <span className="landing-qa-question-label">{item.question}</span>
                    </span>
                    <span className="landing-qa-chevron" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                        <path
                          d="M6 9l6 6 6-6"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  {isOpen ? (
                    <div className="landing-qa-answer">
                      <p className="landing-qa-answer-text">{item.answer}</p>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </main>

      <PlatformCompanyInfo reveal={false} />
    </div>
  );
};

export default LandingQaPage;
